import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/system";
import { useState } from "react";
import SEO from "../../components/SEO";
import { useLeadGeneration } from "../../hooks/useLeadGeneration";
import TalkToAdviser from "../Popups/TalkToAdviser";
import ThankYouPopup from "../Popups/ThankYouPopup";

const advisoryServices = [
  {
    number: "1",
    title: "Business Plan and Project Report Development",
    description:
      "Secure the funding you need and chart a clear path to growth with a professional Business Plan or Project Report. We'll work with you to create a comprehensive document that not only outlines your vision but also includes detailed market analysis, financial projections, and strategic goals. This report will be tailored to meet the specific requirements of banks, investors, and other financial institutions, significantly increasing your chances of success.",
    img: "/SMEAdvisory1.jpg",
  },
  {
    number: "2",
    title: "Budgeting and Forecasting",
    description:
      "Gain a firm grip on your company's financial future. Our Budgeting and Forecasting service helps you move beyond guesswork by creating realistic financial models. We'll assist you in developing accurate budgets and forecasts that provide clarity on your cash flow, profitability, and resource allocation, empowering you to make confident, data-driven decisions for sustainable growth.",
    img: "/SMEAdvisory2.png",
  },
  {
    number: "3",
    title: "Pre-Balance Sheet Finalization for Banking Compliance",
    description:
      "Ensure your financial statements are lender-ready with our Pre-Balance Sheet Finalization service. Before your final accounts are prepared, we'll analyze your key financial ratios—such as debt-to-equity, current ratio, and debt service coverage ratio—to ensure they align with banking requirements. We'll provide strategic recommendations to optimize your balance sheet, helping you present a strong and compliant financial profile to banks and other lenders.",
    img: "/SMEAdvisory3.jpg",
  },
  {
    number: "4",
    title: "Alternative Financing Solutions",
    description:
      "Don't let traditional banking limitations hold you back. Our Alternative Financing advisory service helps you explore a wider range of capital sources beyond conventional bank loans. We'll identify and connect you with suitable options, including venture capital, angel investors, private equity, and government-backed schemes, giving your business the flexibility and resources it needs to thrive.",
    img: "/SMEAdvisory4.jpg",
  },
];

const StepNumber = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  marginRight: "10px",
  fontSize: "1.5rem",
  fontWeight: "bold",
  color: "#18CB96",
  zIndex: 10,
}));

const SMEAdvisory = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [showTalkToAdviser, setShowTalkToAdviser] = useState(false);
  const [showThankYouPopUp, setShowThankYouPopUp] = useState(false);
  const handleLeadGeneration = useLeadGeneration(
    setShowTalkToAdviser,
    setShowThankYouPopUp
  );

  return (
    <>
      <SEO
        title="SME Advisory Services - Strategic Financial Advisory | Dequitec"
        description="Expert SME advisory services including business plan development, budgeting, forecasting, balance sheet optimization, and alternative financing solutions."
        canonical="https://www.dequitec.com/sme-advisory"
      />
      <Container maxWidth="lg" sx={{ py: 5, position: "relative", mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            marginTop: "1%",
          }}
        >
          <Box
            sx={{ width: "40px", height: "2px", backgroundColor: "#004AAD" }}
          />
          <Typography
            variant="h8"
            sx={{ fontWeight: "bold", color: "#004AAD" }}
          >
            SME Advisory
          </Typography>
        </Box>

        <Grid
          container
          spacing={8}
          alignItems="center"
          justifyContent={"center"}
        >
          <Grid item xs={12} md={6} lg={3}>
            <Paper elevation={0} sx={{ borderRadius: 2, padding: 4 }}>
              <img
                src="/AboutUsBanner.webp"
                alt="SME Advisory"
                style={{
                  height: "22vh",
                  borderRadius: "12px",
                  objectFit: "contain",
                  width: "100%",
                }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "#004AAD", mb: 2 }}
            >
              Beyond Lending: Your Partner in SME Growth
            </Typography>
            <Typography style={{ fontSize: "0.9rem", fontWeight: 100 }}>
              In addition to helping you secure the capital you need, we provide
              expert advisory to build a stronger, more resilient business. We
              believe that smart financial strategy is the key to sustainable
              success, which is why we offer a suite of services designed to
              help you navigate the complexities of running a business.
            </Typography>

            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "flex-start",
                zIndex: 10,
                position: "relative",
              }}
            >
              <Button
                variant="contained"
                onClick={handleLeadGeneration}
                startIcon={<RecordVoiceOverIcon />}
                sx={{
                  backgroundColor: "#18CB96",
                  color: "#fff",
                  py: 1.5,
                  px: 3,
                  textTransform: "none",
                  fontSize: "1rem",
                  borderRadius: "8px",
                  fontWeight: 600,
                  cursor: "pointer",
                  zIndex: 10,
                  position: "relative",
                  "&:hover": {
                    backgroundColor: "#14B384",
                    cursor: "pointer",
                  },
                  boxShadow: "0 2px 8px rgba(24, 203, 150, 0.3)",
                }}
              >
                Schedule a Free Consultation
              </Button>
            </Box>
          </Grid>
        </Grid>

        {advisoryServices.map((service, index) => (
          <Grid
            container
            spacing={3}
            key={index}
            alignItems="center"
            justifyContent="center"
            sx={{ position: "relative", py: 5 }}
            direction={isSmallScreen ? "column" : "row"}
          >
            {isSmallScreen && (
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 5,
                  mb: 2,
                }}
              >
                <img
                  src={service.img}
                  alt={service.title}
                  style={{
                    width: "70%",
                    maxWidth:
                      theme.breakpoints.values.sm > 0 ? "350px" : "250px",
                  }}
                />
              </Grid>
            )}

            {!isSmallScreen && index % 2 === 0 ? (
              <>
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <StepNumber>{service.number}</StepNumber>
                    <Typography variant="h5" color="#004AAD">
                      {service.title}
                    </Typography>
                  </Box>
                  <>
                    {service.description.split("\n").map((line, idx) => (
                      <Typography key={idx} sx={{ mb: 1 }}>
                        {line.trim()}
                      </Typography>
                    ))}
                  </>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 5,
                  }}
                >
                  <img
                    src={service.img}
                    alt={service.title}
                    style={{ height: "50%", width: "50%" }}
                  />
                </Grid>
              </>
            ) : !isSmallScreen ? (
              <>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 5,
                  }}
                >
                  <img
                    src={service.img}
                    alt={service.title}
                    style={{ height: "50%", width: "50%" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <StepNumber>{service.number}</StepNumber>
                    <Typography variant="h5" color="#004AAD">
                      {service.title}
                    </Typography>
                  </Box>
                  <>
                    {service.description.split("\n").map((line, idx) => (
                      <Typography key={idx} sx={{ mb: 1 }}>
                        {line.trim()}
                      </Typography>
                    ))}
                  </>
                </Grid>
              </>
            ) : null}

            {isSmallScreen && (
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 5,
                  textAlign: "center",
                  px: { xs: 2, sm: 4 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <StepNumber>{service.number}</StepNumber>
                  <Typography variant="h5" color="#004AAD">
                    {service.title}
                  </Typography>
                </Box>
                <Typography>{service.description}</Typography>
              </Grid>
            )}
          </Grid>
        ))}

        {/* TalkToAdviser and ThankYou Popups */}
        {showTalkToAdviser && (
          <TalkToAdviser
            showTalkToAdviser={showTalkToAdviser}
            setShowTalkToAdviser={setShowTalkToAdviser}
            setShowThankYouPopUp={setShowThankYouPopUp}
          />
        )}
        {showThankYouPopUp && (
          <ThankYouPopup
            showThankYouPopUp={showThankYouPopUp}
            setShowThankYouPopUp={setShowThankYouPopUp}
            from="sme-advisory"
          />
        )}
      </Container>
    </>
  );
};

export default SMEAdvisory;
